import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import api from '../../../axios/axiosConfig';
import { useParams } from 'react-router-dom';
import calenderIcon from '../../../assets/calender-icon.png';
import alertIcon from '../../../assets/alert-icon.png';

import { HeaderViewer } from '../../../components/header-viewer/HeaderViewer';
import { FileResponse } from '../../charger-status/types';

import { SelectButtonComponent } from './components/SelectButton';
import MapViewerComponent from './components/Map';
import ViewerSkeleton from './components/Skeleton';
import { FleetNotFoundComponent } from './components/FleetNotFound';

import { ChargerStatusPage } from './ChargerStatus';
import { RechargePriorityPage } from './RechargePriority';

import {
  PAGE_DEFAULT_CONFIG,
  PAGE_INFO_CONFIGS,
  PAGE_OPTIONS_CONFIG,
} from './configs/utils';

import './index.css';

import ViewerTableSkeleton from './components/Skeleton/table';
import { Footer } from './components/Footer';

export const ViewerContainerPage = () => {
  const { id = '', page = '' } = useParams();

  /* types page: charger-status | recharge-priority | fleet-map */
  const [responseData, setResponseData] = useState<any>();
  const [isDataReady, setIsDataReady] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [background, setBackground] = useState<string>(
    PAGE_DEFAULT_CONFIG?.background
  );
  const [bgColor, setBgColor] = useState<string>(PAGE_DEFAULT_CONFIG?.bgColor);
  const [logo, setLogo] = useState<string>(PAGE_DEFAULT_CONFIG?.logo);
  const [title, setTitle] = useState<string>(PAGE_DEFAULT_CONFIG?.title);
  const [selectViewer, serSelectViewer] = useState<string>(page);
  const currentDate = moment().format('DD/MM/YYYY - HH:mm');

  // configuração da página, da opção selecionada
  const fleetCurrentConfig: any = useMemo(
    () => PAGE_INFO_CONFIGS[selectViewer],
    [selectViewer]
  );

  // configuração atual da opção selecionada
  const fleetCurrentOption: any = useMemo(
    () => PAGE_OPTIONS_CONFIG.find((p: any) => p.id === selectViewer),
    [selectViewer]
  );

  // buscar os dados da página
  const getResponseData = useCallback(async () => {
    try {
      setIsFetching(true);
      let tableData: any = {
        csBgColor: null,
        csBgFileId: null,
        csLogoFileId: null,
      };
      if (fleetCurrentOption?.service) {
        const { data }: any = await api.get(
          `${fleetCurrentOption?.service}${id}`
        );
        if (Array.isArray(data))
          tableData[fleetCurrentOption?.dataColumn] = data;
        else tableData = data;
      }

      const { csBgColor, csBgFileId, csLogoFileId } = tableData;

      let headerData = {};

      if (fleetCurrentOption?.serviceHeader) {
        // se não tiver dados de LOGO | COR chamar serviço específico
        if (!csBgColor || !csBgFileId || !csLogoFileId) {
          const { data: dataHeaderResponse }: any = await api.get(
            `${fleetCurrentOption?.serviceHeader}${id}`
          );
          headerData = dataHeaderResponse;
        }
      }
      let mapData: any = {
        vehicles: [],
        connectors: [],
      };

      const mapDataCount: any = {
        vehiclesCount: 0,
        connectorsCount: 0,
      };

      // busca os dados para o mapa
      if (fleetCurrentOption?.serviceMap?.length) {
        const services = fleetCurrentOption?.serviceMap;
        for (let index = 0; index < services.length; index++) {
          const { data } = await api.get(`${services[index].service}${id}`);
          mapData[services[index].id] = data;
          mapDataCount[services[index].id + 'Count'] = data?.length;
        }
      }

      setResponseData({
        ...tableData,
        ...headerData,
        ...(fleetCurrentOption?.serviceMap?.length > 0 ? mapDataCount : {}), // só adiciona se tiver length > 0
        mapData,
      });
      setIsDataReady(true);
    } catch (error) {
      console.error(error);
      setHasError(true);
    } finally {
      setIsFetching(false);
    }
  }, [
    fleetCurrentOption?.dataColumn,
    fleetCurrentOption?.service,
    fleetCurrentOption?.serviceHeader,
    fleetCurrentOption?.serviceMap,
    id,
  ]);

  // buscar logo
  const getFile = useCallback(
    async (
      fileId: string,
      setter: React.Dispatch<React.SetStateAction<string>>
    ) => {
      try {
        const { data } = await api.get<FileResponse>(
          `/Files/${String(fileId)}`
        );
        if (data) setter(data.fileData);
      } catch (err) {
        console.log(err);
      }
    },
    []
  );

  const renderContentPage = useCallback(() => {
    switch (selectViewer) {
      case 'charger-status':
        return isFetching ? (
          <ViewerTableSkeleton />
        ) : (
          <ChargerStatusPage data={responseData?.connectors} />
        );

      case 'recharge-priority':
        return isFetching ? (
          <ViewerTableSkeleton />
        ) : (
          <RechargePriorityPage data={responseData?.vehicles} />
        );

      case 'fleet-map':
        return (
          <MapViewerComponent
            mapData={responseData?.mapData}
            brandColor={bgColor}
          />
        );

      default:
        break;
    }
  }, [
    bgColor,
    isFetching,
    responseData?.connectors,
    responseData?.mapData,
    responseData?.vehicles,
    selectViewer,
  ]);

  // buscar dados iniciais da página
  useEffect(() => {
    getResponseData();
  }, [getResponseData]);

  // request para buscar LOGO | COR
  useEffect(() => {
    const { csLogoFileId, csBgFileId, csBgColor, vwpDescription } =
      responseData || {};
    if (csLogoFileId) getFile(csLogoFileId, setLogo);
    if (csBgFileId) getFile(csBgFileId, setBackground);
    if (csBgColor) setBgColor(csBgColor);
    if (vwpDescription) setTitle(vwpDescription);
  }, [responseData, getFile]);

  return (
    <Fragment>
      <div
        style={{
          padding: '0px 16px',
          paddingBottom: '28px',
        }}
      >
        <div className='vp-charger-status'>
          {isFetching && !isDataReady ? (
            <ViewerSkeleton />
          ) : !fleetCurrentOption ? (
            <FleetNotFoundComponent
              messageError='Página ou Frota Não Encontrada'
              title={`Verifique o parâmetro "${page}" na URL.`}
              subtitle='O ID da página que você tentou acessar pode não estar configurado ou está  incorreto.'
            />
          ) : hasError ? (
            <FleetNotFoundComponent
              messageError='Falha ao Carregar Dados'
              title={`Não foi possível buscar as informações para o ID  "${id}".`}
              subtitle='Verifique se o ID está correto ou se a requisição está falhando.'
            />
          ) : (
            <>
              <HeaderViewer
                logo={logo}
                background={background}
                bgColor={bgColor}
              />
              <Fragment>
                <div className='vp-wrapper-date'>
                  <img
                    src={calenderIcon}
                    alt='Data/Hora'
                    className='vp-calender-icon'
                  />
                  <span>{currentDate}</span>
                </div>
                <div className='vp-header-charger-status'>
                  <img
                    src={fleetCurrentConfig?.icon}
                    alt={title}
                    className='vp-charger-station-logo-image'
                  />
                  <p className='vp-local-title'>{title}</p>
                </div>
                <SelectButtonComponent
                  selected={selectViewer}
                  setSelected={serSelectViewer}
                  pageId={id}
                  options={PAGE_OPTIONS_CONFIG}
                />
                <div className='vp-chargers-wrapper'>
                  <div className='vp-chargers-header'>
                    <span className='vp-chargers-header-title'>
                      {fleetCurrentConfig?.headerTitle}
                    </span>
                    {fleetCurrentConfig?.headerLabelProps?.map(
                      (labelConfig: any, labelIndex: number) => (
                        <div
                          className='vp-total-chargers-wrapper'
                          key={labelIndex}
                        >
                          <span className='vp-total-chargers-text'>
                            {labelConfig.label}
                          </span>

                          {labelConfig?.render ? (
                            labelConfig?.render(responseData)
                          ) : (
                            <span className='vp-total-number-of-chargers'>
                              {responseData?.[labelConfig.prop]}
                            </span>
                          )}
                        </div>
                      )
                    )}
                  </div>
                  <div className='vp-gradient' />
                  {renderContentPage()}
                </div>
              </Fragment>
              <div className='cs-footer'>
                <img
                  src={alertIcon}
                  alt={`${alertIcon}`}
                  className='cs-table-icon'
                />
                <div>
                  <span>
                    <strong>Problemas com o carregador, </strong>
                    Suporte por e-mail{' '}
                    <a
                      style={{ color: 'green' }}
                      href='mailto:suporte@spott.eco'
                    >
                      suporte@spott.eco
                    </a>
                    <strong> ou por</strong>{' '}
                    <a
                      style={{ color: 'green' }}
                      href='https://wa.me/5511975568167'
                    >
                      WhatsApp
                    </a>{' '}
                    - (11) 97556-8167
                  </span>
                </div>
                <Footer />
              </div>
            </>
          )}
        </div>
      </div>
    </Fragment>
  );
};
